import type { NonNullableFormBuilder } from '@angular/forms';
import type { OnyxHttpErrorResponse, OnyxToastOptions } from '@onyx/angular';
import { NoteFormComponent } from '../../../../common/components/forms/note-form/note-form.component';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import type { JoinPipe } from '../../../../common/pipes/join.pipe';
import { DelegationDailyReturnsFormComponent } from '../../delegation-form/delegation-daily-returns-form/delegation-daily-returns-form.component';
import { DelegationDriverAndVehicleFormComponent } from '../../delegation-form/delegation-driver-and-vehicle-form/delegation-driver-and-vehicle-form.component';
import { DelegationEndFormComponent } from '../../delegation-form/delegation-end-form/delegation-end-form.component';
import { DelegationStartFormComponent } from '../../delegation-form/delegation-start-form/delegation-start-form.component';
import type { Delegation } from '../interfaces/delegation';

export type DelegationForm = ReturnType<
  ReturnType<typeof DelegationFormHelper.buildForm>['getRawValue']
>;

export interface DelegationDto {
  primaryDriverUuid: string;
  secondaryDriverUuid: string | null;
  vehicleUuid: string;
  trailerUuid: string | null;
  start: DelegationForm['start'];
  end: DelegationForm['end'];
  isReturning: boolean;
  hasDailyReturns: DelegationForm['dailyReturns']['hasDailyReturns'];
  daysOfWeek: DelegationForm['dailyReturns']['daysOfWeek'];
  note: DelegationForm['note'];
}

export class DelegationFormHelper {
  public static getDelegationErrorToasts(
    response: OnyxHttpErrorResponse,
    joinPipe: JoinPipe,
  ): OnyxToastOptions[] {
    const { status, error } = response;
    if (!VALIDATION_ERROR_CODES.includes(status)) return [];

    const ERRORS_MAP: [string, OnyxToastOptions][] = [
      [
        'Only delegations without assigned vehicle route points can be finished',
        { message: 'delegations.cannotFinishDelegationWithRoute' },
      ],
      [
        'Only delegations without vehicle route points can be cancelled',
        {
          message: 'delegations.cannotCancelDelegationWithRoute',
          description: 'delegations.chooseFinishOption',
        },
      ],
    ];

    const toasts: OnyxToastOptions[] = [];
    const { vehicle, trailer, primaryDriver, secondaryDriver } = error ?? {};
    const reason = error?.status?.reason ?? error?.reason;

    for (const [expectedReason, toast] of ERRORS_MAP) {
      if (reason === expectedReason) toasts.push(toast);
    }

    const overlappingErrorsMap = [
      [vehicle, 'vehicleHasDelegation'],
      [trailer, 'trailerHasDelegation'],
      [primaryDriver, 'primaryDriverHasDelegation'],
      [secondaryDriver, 'secondaryDriverHasDelegation'],
    ];

    for (const [data, key] of overlappingErrorsMap) {
      if (
        !data?.reason?.startsWith(
          'Object has other delegations in provided date range',
        )
      ) {
        continue;
      }

      const identifiers = (data?.delegations ?? []).map(
        (delegation: Delegation) => delegation.identifier,
      );

      toasts.push({
        message: `delegations.delegationsForm.${key}`,
        description: joinPipe.transform(identifiers, {
          separator: ', ',
          maxLength: 2,
        }),
      });
    }

    const errorsMap: [string, string, string][] = [
      [
        primaryDriver,
        'Driver has unavailability in provided date range.',
        'primaryDriverHasUnavailability',
      ],
      [
        secondaryDriver,
        'Driver has unavailability in provided date range.',
        'secondaryDriverHasUnavailability',
      ],
    ];

    for (const [response, expectedResponse, key] of errorsMap) {
      if (response === expectedResponse) {
        toasts.push({ message: `delegations.delegationsForm.${key}` });
      }
    }

    return toasts;
  }

  public static fromDto(dto: Delegation): DelegationForm {
    return {
      driverAndVehicle: {
        primaryDriverUuid: dto.primaryDriver.uuid,
        secondaryDriverUuid: dto.secondaryDriver?.uuid ?? null,
        vehicleUuid: dto.vehicle.uuid,
        trailerUuid: dto.trailer?.uuid ?? null,
      },
      dailyReturns: {
        daysOfWeek: dto.daysOfWeek,
        hasDailyReturns: dto.hasDailyReturns,
        hasSelectedDays: dto.daysOfWeek != null,
      },
      start: {
        departurePlace: dto.start.address,
        departureDate: dto.start.date,
        departureTime: dto.start.time,
      },
      end: {
        arrivalDate: dto.end.date,
        arrivalPlace: dto.end.address,
        arrivalTime: dto.end.time,
      },
      isReturning: dto.isReturning,
      note: { content: dto.note?.content ?? null },
    };
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      driverAndVehicle: DelegationDriverAndVehicleFormComponent.buildForm(fb),
      dailyReturns: DelegationDailyReturnsFormComponent.buildForm(fb),
      start: DelegationStartFormComponent.buildForm(fb),
      end: DelegationEndFormComponent.buildForm(fb),
      isReturning: fb.control(false),
      note: NoteFormComponent.buildForm(fb),
    });
  }
}
